/**
 * Date Range Utilities
 *
 * Utility functions for calculating date ranges based on presets.
 */

import type { DateRangePreset } from '../types/financialInsights';

/**
 * Calculate start and end dates for a given preset
 */
export const getDateRangeForPreset = (
  preset: DateRangePreset
): { startDate: string; endDate: string } | null => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentQuarter = Math.floor(currentMonth / 3);

  let startDate: Date;
  let endDate: Date = today;

  switch (preset) {
    case 'this_month':
      startDate = new Date(currentYear, currentMonth, 1);
      endDate = new Date(currentYear, currentMonth + 1, 0);
      break;

    case 'last_month':
      startDate = new Date(currentYear, currentMonth - 1, 1);
      endDate = new Date(currentYear, currentMonth, 0);
      break;

    case 'qtd':
      // Quarter to Date
      startDate = new Date(currentYear, currentQuarter * 3, 1);
      endDate = today;
      break;

    case 'ytd':
      // Year to Date
      startDate = new Date(currentYear, 0, 1);
      endDate = today;
      break;

    case 'fy':
      // Fiscal Year (assuming calendar year)
      startDate = new Date(currentYear, 0, 1);
      endDate = new Date(currentYear, 11, 31);
      break;

    case 'custom':
      // For custom, return null (no default calculation)
      return null;

    default:
      return null;
  }

  // Format dates as YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};
